import { localize } from "@/i18n/translate";
import verificationDE from "@/i18n/locales/verification.de.json";
import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2, Mail } from "lucide-react";
import { errorCode } from "@/lib/api";
import { useAuth } from "../AuthContext";
import { usePortal } from "../i18n";
import { AuthLayout } from "../AuthLayout";
import { validRegistrationPhone, PHONE_TEXT } from "../registrationPhone";
import { NAME_TEXT } from "../registrationName";
import { ErrorNote, FormField, btnPrimary, inputCls } from "../ui";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PENDING_KEY = 'sba-email-verification';
const initialChallenge = () => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null');
    return saved?.expiresAt > Date.now() && /^[a-f0-9]{32}$/.test(saved.challenge_id) ? saved : null;
  } catch { return null; }
};
const EMPTY = {
  first_name: "",
  last_name: "",
  organisation: "",
  phone: "",
  email: "",
  password: "",
  password2: "",
  consent: false
};
export default function Register() {
  const {
    user,
    register,
    verifyEmail,
    resendEmail
  } = useAuth();
  const {
    p,
    lang,
    errText
  } = usePortal();
  const navigate = useNavigate();
  const verificationText = value => lang === 'de' ? verificationDE[value] || value : localize(value);
  const [v, setV] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState(initialChallenge);
  const [code, setCode] = useState('');
  const [info, setInfo] = useState('');
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (!challenge) return undefined;
    const tick = () => setSeconds(Math.max(0, Math.ceil((challenge.resendAt - Date.now()) / 1000)));
    tick(); const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [challenge]);
  const rememberChallenge = data => {
    const pending = { ...data, expiresAt: Date.now() + data.expires_in * 1000, resendAt: Date.now() + data.resend_after * 1000 };
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    setChallenge(pending);
  };
  const verificationError = ex => {
    const messages = {
      verification_unavailable: 'Email verification is temporarily unavailable.',
      verification_invalid: 'That code is incorrect. Please try again.',
      verification_expired: 'Your code has expired. Register again to receive a new code.',
      verification_locked: 'Too many incorrect attempts. Request a new code.',
      verification_wait: 'Please wait before requesting another code.',
      verification_rate_limited: 'Too many requests. Please try again later.',
      password_too_long: 'Please use a shorter password.'
    };
    return verificationText(messages[errorCode(ex)] || errText(errorCode(ex)));
  };
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/kundenbereich"} replace />;
  const e_ = p.auth.err;
  const phoneText = PHONE_TEXT[lang] || PHONE_TEXT.en;
  const nameText = NAME_TEXT[lang] || NAME_TEXT.en;
  const validate = () => {
    const r = {};
    if (!v.first_name.trim()) r.first_name = nameText.firstError;
    if (!v.last_name.trim()) r.last_name = nameText.lastError;
    if (!validRegistrationPhone(v.phone)) r.phone = phoneText.error;
    if (!EMAIL_RE.test(v.email.trim())) r.email = e_.email;
    if (v.password.length < 8) r.password = e_.password;
    if (v.password2 !== v.password) r.password2 = e_.password2;
    if (!v.consent) r.consent = e_.consent;
    return r;
  };
  const submit = async e => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return document.getElementById(`reg-${Object.keys(errs)[0]}`)?.focus();
    setBusy(true);
    setErr(null);
    try {
      const result = await register({
        first_name: v.first_name.trim(),
        last_name: v.last_name.trim(),
        organisation: v.organisation.trim(),
        phone: v.phone.trim(),
        email: v.email.trim(),
        password: v.password
      });
      if (result.verification_required) {
        rememberChallenge(result);
        setV(current => ({ ...current, password: '', password2: '' }));
        setBusy(false);
        return;
      }
      navigate("/kundenbereich", {
        replace: true
      });
    } catch (ex) {
      setErr(verificationError(ex));
      setBusy(false);
    }
  };
  const confirmEmail = async event => {
    event.preventDefault();
    if (busy) return;
    if (!/^[0-9]{6}$/.test(code)) { setErr(verificationText('Enter the six-digit code from your email.')); return; }
    setBusy(true); setErr(null); setInfo('');
    try {
      await verifyEmail(challenge.challenge_id, code);
      sessionStorage.removeItem(PENDING_KEY);
      navigate('/kundenbereich', { replace: true });
    } catch (ex) { setErr(verificationError(ex)); setBusy(false); }
  };
  const resendCode = async () => {
    if (busy || seconds > 0) return;
    setBusy(true); setErr(null); setInfo('');
    try {
      rememberChallenge(await resendEmail(challenge.challenge_id));
      setCode(''); setInfo(verificationText('Code sent. Check your inbox and spam folder.'));
    } catch (ex) { setErr(verificationError(ex)); }
    finally { setBusy(false); }
  };
  if (challenge) return <AuthLayout>
    <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-signal/10 text-signal"><Mail className="h-6 w-6" aria-hidden="true" /></div>
    <p className="eyebrow">{localize(p.area)}</p>
    <h1 className="mt-4 font-display text-4xl font-extrabold tracking-[-0.03em]">{verificationText('Check your email')}</h1>
    <p className="mt-4 leading-relaxed text-mute">{verificationText('We sent a six-digit code to')}<br /><strong className="break-all text-ink">{challenge.email}</strong></p>
    <form onSubmit={confirmEmail} className="mt-8 space-y-5" data-testid="email-verification-form">
      <ErrorNote testId="verification-error">{err}</ErrorNote>
      <FormField id="email-verification-code" label={verificationText('Verification code')}>
        <input id="email-verification-code" name="code" inputMode="numeric" pattern="[0-9]{6}" value={code} onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} autoComplete="one-time-code" autoCapitalize="characters" spellCheck={false} maxLength={6} required autoFocus aria-invalid={Boolean(err)} className={`${inputCls(Boolean(err))} text-center font-mono !text-3xl uppercase tracking-[0.4em]`} data-testid="email-verification-code" />
      </FormField>
      <p className="text-xs leading-relaxed text-mute">{verificationText('Your code expires in 10 minutes.')}</p>
      <button type="submit" disabled={busy || code.length !== 6} className={`${btnPrimary} w-full`} data-testid="verify-email-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}{verificationText('Verify email')}</button>
      {info && <p role="status" className="text-sm text-mute">{info}</p>}
    </form>
    <div className="mt-6 flex flex-wrap items-center gap-3 text-sm">
      <button type="button" onClick={resendCode} disabled={busy || seconds > 0} className="font-medium underline underline-offset-4 disabled:cursor-not-allowed disabled:opacity-50" data-testid="resend-email-code">{verificationText('Resend code')}</button>
      {seconds > 0 && <span className="text-xs text-mute">{verificationText('Resend available in')} {seconds} {verificationText('seconds')}</span>}
    </div>
    <button type="button" disabled={busy} onClick={() => { sessionStorage.removeItem(PENDING_KEY); setChallenge(null); setCode(''); setErr(null); setInfo(''); }} className="mt-6 text-sm text-mute underline underline-offset-4">{verificationText('Use a different email')}</button>
  </AuthLayout>;
  const field = (k, type = "text", auto) => <input id={`reg-${k}`} type={type} autoComplete={auto} required={['phone','first_name','last_name','email'].includes(k)} maxLength={k === 'phone' ? 40 : ['first_name','last_name'].includes(k) ? 60 : undefined} value={v[k]} onChange={e => {
    setV({
      ...v,
      [k]: e.target.value
    });
    if (errors[k]) setErrors({
      ...errors,
      [k]: undefined
    });
  }} className={inputCls(errors[k])} data-testid={`register-${k}-input`} aria-invalid={errors[k] ? true : undefined} aria-describedby={errors[k] ? `reg-${k}-error` : undefined} />;
  return <AuthLayout>
      <p className="eyebrow">{localize(p.area)}</p>
      <h1 className="mt-4 font-display text-4xl font-extrabold tracking-[-0.03em] sm:text-5xl">{localize(p.auth.registerTitle)}</h1>
      <p className="mt-4 leading-relaxed text-mute">{localize(p.auth.registerIntro)}</p>
      <form onSubmit={submit} noValidate className="mt-10 space-y-6" data-testid="register-form">
        <ErrorNote testId="register-error">{localize(err)}</ErrorNote>
        <div className="grid gap-6 sm:grid-cols-2">
          <FormField id="reg-first_name" label={nameText.first} hint={phoneText.required} error={errors.first_name}>{field("first_name", "text", "given-name")}</FormField>
          <FormField id="reg-last_name" label={nameText.last} hint={phoneText.required} error={errors.last_name}>{field("last_name", "text", "family-name")}</FormField>
        </div>
        <div className="grid gap-6 sm:grid-cols-2">
          <FormField id="reg-organisation" label={p.auth.org} hint={p.auth.optional}>{localize(field("organisation", "text", "organization"))}</FormField>
          <FormField id="reg-phone" label={p.auth.phone} hint={phoneText.required} error={errors.phone}>{localize(field("phone", "tel", "tel"))}</FormField>
        </div>
        <FormField id="reg-email" label={p.auth.email} hint={phoneText.required} error={errors.email}>{localize(field("email", "email", "email"))}</FormField>
        <FormField id="reg-password" label={p.auth.password} hint={p.auth.passwordHint} error={errors.password}>{localize(field("password", "password", "new-password"))}</FormField>
        <FormField id="reg-password2" label={p.auth.password2} error={errors.password2}>{localize(field("password2", "password", "new-password"))}</FormField>
        <div>
          <label htmlFor="reg-consent" className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-ink">
            <input id="reg-consent" type="checkbox" checked={v.consent} onChange={e => {
            setV({
              ...v,
              consent: e.target.checked
            });
            if (errors.consent) setErrors({
              ...errors,
              consent: undefined
            });
          }} className="mt-0.5 h-5 w-5 shrink-0 accent-[#D52B1E]" data-testid="register-consent-checkbox" aria-invalid={errors.consent ? true : undefined} aria-describedby={errors.consent ? "reg-consent-error" : undefined} />
            <span>{localize(p.auth.consentA)}<Link to="/datenschutz" className="underline underline-offset-4 hover:text-signal">{localize(p.auth.consentLink)}</Link>{localize(p.auth.consentB)}</span>
          </label>
          {localize(errors.consent && <p id="reg-consent-error" className="mt-2 text-sm text-signaldark" data-testid="reg-consent-error">{localize(errors.consent)}</p>)}
        </div>
        <button type="submit" disabled={busy} className={`${btnPrimary} w-full`} data-testid="register-submit-btn">
          {localize(busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />)} {localize(p.auth.submitRegister)}
        </button>
      </form>
      <p className="mt-8 text-sm text-mute">
        {localize(p.auth.hasAccount)}{localize(" ")}
        <Link to="/kundenbereich/anmelden" className="font-medium text-ink underline underline-offset-4 hover:text-signal" data-testid="register-to-login">{localize(p.auth.toLogin)}</Link>
      </p>
    </AuthLayout>;
}
